import { ChevronDown } from 'lucide-react'
import { useCallback, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { OptionList } from './listbox'
import { UMBRAL_DE_BUSQUEDA, optionId, useListNav, type ListOption } from './listbox-nav'
import { Popover } from './Popover'
import { CONTROL } from './Select'

export interface MultiSelectProps {
  /** Nombre accesible del control: "Empresa", "Fase". */
  label: string
  /** Lo que dice el disparador sin nada elegido: "Todas las empresas". */
  placeholder: string
  options: readonly ListOption[]
  values: readonly string[]
  onChange: (values: string[]) => void
  disabled?: boolean
  className?: string
}

/**
 * Un filtro de varios valores: "Nube Andina **o** Grupo Barrantes".
 *
 * Nada elegido es "todos", no "ninguno": es lo que dice el disparador y lo que
 * hace el filtro. Elegir no cierra el panel —se eligen varias de un tirón— y
 * el orden de los valores es el de las opciones, no el de los clics, así la
 * misma selección produce siempre la misma URL.
 */
export function MultiSelect({
  label,
  placeholder,
  options,
  values,
  onChange,
  disabled,
  className,
}: MultiSelectProps) {
  const t = useT()
  const boton = useRef<HTMLButtonElement>(null)
  const listId = `${useId()}-lista`
  const [open, setOpen] = useState(false)
  const nav = useListNav(options, open, 0)
  const searchable = options.length > UMBRAL_DE_BUSQUEDA
  const elegidos = useMemo(() => new Set(values), [values])
  const cerrar = useCallback(() => setOpen(false), [])

  const alternar = (opcion: ListOption) => {
    const siguiente = new Set(elegidos)
    if (siguiente.has(opcion.value)) siguiente.delete(opcion.value)
    else siguiente.add(opcion.value)
    onChange(options.filter((o) => siguiente.has(o.value)).map((o) => o.value))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        setOpen(true)
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      boton.current?.focus()
      return
    }
    if (event.key === 'Tab') {
      setOpen(false)
      return
    }
    if (event.key === ' ' && !searchable) {
      event.preventDefault()
      const opcion = nav.visibles[nav.active]
      if (opcion && !opcion.disabled) alternar(opcion)
      return
    }
    if (nav.onKey(event, alternar)) event.preventDefault()
  }

  const primero = options.find((o) => elegidos.has(o.value))
  const resumen =
    values.length === 0 || !primero ? (
      <span className="truncate">{placeholder}</span>
    ) : (
      <>
        {primero.leading}
        <span className="min-w-0 truncate">{primero.label}</span>
        {values.length > 1 ? (
          <span className="num shrink-0 rounded-full bg-surface-2 px-1.5 text-2xs text-text-2">
            +{values.length - 1}
          </span>
        ) : null}
      </>
    )

  return (
    <>
      <button
        ref={boton}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && !searchable ? optionId(listId, nav.active) : undefined}
        aria-label={label}
        // El nombre dice QUÉ filtra; la descripción, qué está elegido.
        title={
          values.length > 1
            ? options
                .filter((o) => elegidos.has(o.value))
                .map((o) => o.text)
                .join(' · ')
            : undefined
        }
        disabled={disabled}
        onClick={() => setOpen((abierto) => !abierto)}
        onKeyDown={onKeyDown}
        className={cn(
          CONTROL,
          'flex h-7 w-auto min-w-[150px] items-center gap-1.5 text-left',
          'focus-visible:outline-2 focus-visible:outline-signal',
          values.length > 0 && 'border-signal/60',
          open && 'outline-2 outline-signal',
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">{resumen}</span>
        <ChevronDown size={12} aria-hidden="true" className="shrink-0 text-text-3" />
      </button>

      <Popover anchor={boton} open={open} onClose={cerrar} matchWidth className="max-w-[360px]">
        <OptionList
          id={listId}
          label={label}
          nav={nav}
          multiple
          isSelected={(v) => elegidos.has(v)}
          onPick={alternar}
          searchable={searchable}
          onSearchKeyDown={onKeyDown}
          footer={
            values.length > 0 ? (
              <div className="hairline-t flex justify-end p-1">
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="rounded-[var(--radius-control)] px-2 py-1 text-2xs text-text-2 hover:bg-surface-2 hover:text-text-1"
                >
                  {t('common.select.clear')}
                </button>
              </div>
            ) : null
          }
        />
      </Popover>
    </>
  )
}
